/* ===== game/boss-form-fx.js — 보스 막 · 결계 · 치유 · 날아오른 몸(분사구 · 날개 · 물기둥)의 그림. 판정은 entity/boss-forms ===== */
import { TAU } from '../../engine/core/math.js';
import { mixin } from '../../engine/core/mixin.js';
import { Enemy } from '../entity.js';
import { Game } from '../game.js';
/* 뒤 겹(drops 단계 — 몸 뒤)에 날개 · 분사 불꽃 · 물기둥, 앞 겹(fx 단계)에 막 · 결계 · 치유 빛. 손님도 net 이 준 값으로 똑같이 그린다. */

type C2 = CanvasRenderingContext2D;
const hs = (s: number, i: number) => { const v = Math.sin(s * 127.1 + i * 311.7) * 43758.5453; return v - Math.floor(v); };

/** 원형의 날개 — 자 칼날 같은 상아빛 깃 세 겹(첫째 · 둘째 · 덮깃) · 깃마다 눈금 · 끝을 잇는 점선 설계선 · 뒤에 푸른 빛.
    ★ 빛 선 몇 가닥 부채였을 때 '날개 더 화려하게'(점검판 2026-11-02) — 깃 수 · 겹을 줄이지 말 것. 날갯짓은 겹마다 늦게 따라온다 */
function archeWings(c: C2, x: number, y: number, W: number, T: number, sd: number) {
  const span = Math.max(150, W * 1.7);
  c.save(); c.globalCompositeOperation = 'lighter';
  const g = c.createRadialGradient(x, y, 10, x, y, span * 1.1);
  g.addColorStop(0, 'rgba(150,200,255,.35)'); g.addColorStop(0.5, 'rgba(120,170,255,.12)'); g.addColorStop(1, 'rgba(120,170,255,0)');
  c.fillStyle = g; c.beginPath(); c.ellipse(x, y - span * 0.15, span * 1.1, span * 0.7, 0, 0, TAU); c.fill();
  c.restore();
  for (const s of [-1, 1]) {
    c.save(); c.translate(x + s * W * 0.16, y); c.scale(s, 1);
    const tips: [number, number][] = [];
    const tiers = [[9, 1.0, 0.0, '#f6efdc', 16], [7, 0.68, 0.12, '#e8dcc0', 15], [6, 0.4, 0.22, '#d8ccae', 13]] as const;   // 깃 수 · 길이 · 늦음 · 빛깔 · 폭
    for (let ti = 0; ti < tiers.length; ti++) {
      const [n, len, lag, col, wd] = tiers[ti], flap = Math.sin(T * 3 - lag * 6) * 0.28, spread = 1 + Math.max(0, -Math.sin(T * 3 - lag * 6)) * 0.18;
      for (let i = 0; i < n; i++) {
        const u = i / (n - 1), a = -1.05 + flap + u * 1.25 * spread, L = span * len * (1 - u * 0.45);   // 위 깃이 가장 길고 아래로 짧아진다(겹쳐 한 장 날개로)
        c.save(); c.rotate(a);
        c.beginPath(); c.moveTo(0, -wd * 0.3); c.lineTo(L * 0.8, -wd * 0.5); c.quadraticCurveTo(L * 0.97, -wd * 0.45, L, wd * 0.15); c.lineTo(L * 0.85, wd * 0.55); c.lineTo(0, wd * 0.35); c.closePath();
        c.fillStyle = col; c.fill(); c.strokeStyle = 'rgba(70,90,130,.85)'; c.lineWidth = 1.2; c.stroke();
        c.strokeStyle = 'rgba(70,90,130,.6)'; c.lineWidth = 1;                // 눈금 — 자 칼날
        for (let k = 1; k * 9 < L * 0.9; k++) { const tl = k % 5 === 0 ? wd * 0.5 : wd * 0.25; c.beginPath(); c.moveTo(k * 9, -wd * 0.45); c.lineTo(k * 9, -wd * 0.45 + tl); c.stroke(); }
        const sweep = ((T * 0.9 + u * 0.6 + ti * 0.2 + sd) % 1.4) / 1.4;      // 눈금을 따라 흐르는 빛
        if (sweep < 1) { c.save(); c.globalCompositeOperation = 'lighter'; c.fillStyle = 'rgba(160,215,255,.55)'; c.fillRect(L * sweep - 7, -wd * 0.5, 14, wd); c.restore(); }
        c.restore();
        if (ti === 0) tips.push([Math.cos(a) * L, Math.sin(a) * L]);
      }
    }
    c.save(); c.setLineDash([5, 5]); c.lineDashOffset = -T * 20; c.strokeStyle = 'rgba(140,200,255,.8)'; c.lineWidth = 1.4;   // 깃 끝을 잇는 설계선 · 가름표
    c.beginPath(); tips.forEach(([tx, ty], i) => (i ? c.lineTo(tx, ty) : c.moveTo(tx, ty))); c.stroke(); c.setLineDash([]);
    c.strokeStyle = 'rgba(140,200,255,.9)';
    for (const [tx, ty] of tips) { c.beginPath(); c.moveTo(tx - 4, ty - 4); c.lineTo(tx + 4, ty + 4); c.moveTo(tx + 4, ty - 4); c.lineTo(tx - 4, ty + 4); c.stroke(); }
    c.restore();
    c.save(); c.globalCompositeOperation = 'lighter'; c.fillStyle = 'rgba(190,225,255,.8)';   // 깃 끝에서 떨어지는 설계 먼지
    for (let i = 0; i < 8; i++) { const [tx, ty] = tips[i % tips.length], u = (T * 0.8 + hs(sd + s, i)) % 1; c.fillRect(tx - u * 20, ty + u * 60, 2, 5 - u * 3); }
    c.restore();
    c.restore();
  }
}

export const BossFormFx: Bag = {
  bossesOnScreen() { return this.ents.filter((e: any) => e instanceof Enemy && e.boss && !e.dead && (e.airborne || e.shield > 0 || e.ward > 0 || e.healRun)); },

  /** 몸 뒤 — 날아오른 보스를 받치는 것 */
  drawFormBack(c: C2) {
    const T = this.time, cx0 = this.cam.x, cy0 = this.cam.y;
    for (const e of this.bossesOnScreen()) {
      if (!e.airborne) continue;
      const x = e.cx - cx0, y = e.cy - cy0, foot = e.y + e.h - cy0, W = e.w, H = e.h;
      c.save();
      if (e.airK === 'jets') {                                 // 분사구 둘 — 아래로 뿜는 불꽃(떨리는 길이)
        for (const s of [-1, 1]) {
          const nx = x + s * W * 0.28, L = 46 + Math.sin(T * 40 + s) * 8 + hs(Math.floor(T * 30), s + 2) * 10;
          c.fillStyle = '#3a3430'; c.fillRect(nx - 9, foot - 14, 18, 14);
          c.fillStyle = '#6a5a48'; c.fillRect(nx - 7, foot - 2, 14, 4);
          const g = c.createLinearGradient(0, foot, 0, foot + L);
          g.addColorStop(0, 'rgba(255,250,220,.95)'); g.addColorStop(0.35, 'rgba(255,170,60,.85)'); g.addColorStop(1, 'rgba(255,80,20,0)');
          c.fillStyle = g; c.beginPath(); c.moveTo(nx - 7, foot + 2); c.quadraticCurveTo(nx, foot + L * 1.1, nx + 7, foot + 2); c.fill();
        }
      } else if (e.airK === 'wings') {                         // 설계선 날개(원형) — wingsArche
        archeWings(c, x, y - H * 0.16, W, T, e.nid || 1);
      } else {                                                 // 물기둥 — 발밑에서 아래로 굽이치는 물줄기
        const by = Math.min(foot + 260, (e.floorY ? e.floorY(e.cx, e.y + e.h) : e.y + e.h + 200) - cy0);
        for (let i = 0; i < 3; i++) {
          const w = W * (0.42 - i * 0.1);
          c.fillStyle = ['rgba(60,140,200,.55)', 'rgba(120,190,235,.6)', 'rgba(220,245,255,.7)'][i];
          c.beginPath(); c.moveTo(x - w, foot - 6);
          for (let yy = foot; yy <= by; yy += 10) c.lineTo(x - w * (1 + (yy - foot) / 300) + Math.sin(yy * 0.05 - T * 8 + i) * 5, yy);
          for (let yy = by; yy >= foot; yy -= 10) c.lineTo(x + w * (1 + (yy - foot) / 300) + Math.sin(yy * 0.05 - T * 8 + i + 2) * 5, yy);
          c.closePath(); c.fill();
        }
        c.fillStyle = 'rgba(230,250,255,.8)';
        for (let i = 0; i < 6; i++) { const u = (T * 1.4 + hs(e.nid || 1, i)) % 1; c.beginPath(); c.arc(x + (hs(3, i) - 0.5) * W * 0.8, foot + u * (by - foot), 2.5, 0, TAU); c.fill(); }
      }
      c.restore();
    }
  },

  /** 몸 앞 — 방어 자세 · 막 · 결계 · 치유를 그 보스의 결로(game/boss-theme-fx). 고리 · 육각 껍질 · 룬 고리는 쓰지 않는다 — 사연: docs/code-history.md#h249 */
  drawFormFront(c: C2) {
    const T = this.time, cx0 = this.cam.x, cy0 = this.cam.y, dt = Math.min(0.05, Math.max(0, T - (this._ffT ?? T)));
    this._ffT = T;
    for (const e of this.ents) {
      if (!(e instanceof Enemy) || !e.boss || e.dead) continue;
      const x = e.cx - cx0, y = e.cy - cy0;
      if (x < -e.w * 2 || y < -e.h * 2 || x > this.W + e.w * 2 || y > this.H + e.h * 2) continue;
      this.tfxAura(e, dt);
      const th = this.themeOf(e);
      const glow = (k: number) => {                                // 몸을 따르는 은은한 빛(몸 상자 모양 · 테두리 없음)
        const g = c.createRadialGradient(x, y, Math.min(e.w, e.h) * 0.2, x, y, Math.max(e.w, e.h) * 0.75);
        g.addColorStop(0, th.glow + '00'); g.addColorStop(0.6, th.glow + Math.round(k * 90).toString(16).padStart(2, '0')); g.addColorStop(1, th.glow + '00');
        c.save(); c.globalCompositeOperation = 'lighter'; c.fillStyle = g; c.beginPath(); c.ellipse(x, y, e.w * 0.75, e.h * 0.75, 0, 0, TAU); c.fill(); c.restore();
      };
      if (e.shield > 0) glow(0.35 + 0.1 * Math.sin(T * 4));
      if (e.healRun) glow(0.3 + 0.2 * Math.sin(T * 8));
      if (e.ward > 0) { glow(0.25); this.tfxDrawWard(c, e, cx0, cy0); }
    }
    this.tfxDraw(c);
  },
};
mixin(Game.prototype, BossFormFx, true);
